import type { ReactNode } from 'react';

import { Tooltip } from '../ui';

interface KpiCardProps {
  label: string;
  value: string;
  valueSuffix?: string;
  secondaryValue?: string;
  delta?: { direction: 'up' | 'down'; value: string; context: string };
  icon: ReactNode;
  accentBg?: string;
  tooltip?: string;
  children?: ReactNode;
  /** When true, the value/delta/chart area is replaced with a skeleton. */
  loading?: boolean;
}

export function KpiCard({
  label,
  value,
  valueSuffix,
  secondaryValue,
  delta,
  icon,
  accentBg = 'bg-tide-ss',
  tooltip,
  children,
  loading = false,
}: KpiCardProps) {
  return (
    <div className="bg-paper-hi rounded-lg p-5 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11.5px] text-ink-4 font-medium flex items-center gap-1">
          {label}
          {tooltip && (
            <Tooltip label={tooltip} multiline>
              <svg width="10" height="10" viewBox="0 0 16 16" className="text-ink-5">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <text x="8" y="12" textAnchor="middle" fill="currentColor" fontSize="10" fontWeight="600">i</text>
              </svg>
            </Tooltip>
          )}
        </span>
        <span
          className={`w-6 h-6 rounded ${accentBg} flex items-center justify-center`}
        >
          {icon}
        </span>
      </div>

      {loading ? (
        <>
          <div className="h-[30px] w-28 bg-canvas-2 animate-pulse rounded" />
          <div className="mt-4 h-12 bg-canvas-2 animate-pulse rounded" />
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-[30px] font-medium num">
              {value}
              {valueSuffix && (
                <span className="text-ink-4 text-[22px] font-light">
                  {valueSuffix}
                </span>
              )}
            </span>
            {secondaryValue && (
              <span className="text-[12.5px] text-ink-3 num">{secondaryValue}</span>
            )}
          </div>

          {delta && (
            <div className="mt-1 flex items-center gap-1.5 text-[11.5px]">
              <span
                className={`font-medium ${delta.direction === 'up' ? 'text-sage' : 'text-rust'}`}
              >
                {delta.direction === 'up' ? '↑' : '↓'} {delta.value}
              </span>
              <span className="text-ink-4">{delta.context}</span>
            </div>
          )}

          {children && <div className="mt-4">{children}</div>}
        </>
      )}
    </div>
  );
}
